import React from "react";
import { Box, Container, Typography, styled, Paper, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  HeroSection,
  HeroTitle,
  HeroSubtitle,
} from "../../../components/hero-section/HeroSection";

const ContentPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: "#fff",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3),
  },
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  "&:last-child": {
    marginBottom: 0,
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 600,
  marginBottom: theme.spacing(2),
}));

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';
  
  return (
    <Box>
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <HeroTitle>{t('help.privacyPolicy.title')}</HeroTitle>
              <HeroSubtitle>
                {t('help.privacyPolicy.subtitle')}
              </HeroSubtitle>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      <Box sx={{ py: 6, backgroundColor: "background.default" }}>
        <Container maxWidth="lg">
          <ContentPaper elevation={0}>
            <Section>
              <SectionTitle variant="h5">
                {t('help.privacyPolicy.sections.commitment.title')}
              </SectionTitle>
              <Typography variant="body1" paragraph>
                {t('help.privacyPolicy.sections.commitment.content')}
              </Typography>
            </Section>

            <Section>
              <SectionTitle variant="h5">{t('help.privacyPolicy.sections.collect.title')}</SectionTitle>
              <Typography variant="body1" paragraph>
                {t('help.privacyPolicy.sections.collect.intro')}
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.collect.items.account')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.collect.items.purchase')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.collect.items.usage')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.collect.items.technical')}
                </Typography>
              </Box>
            </Section>

            <Section>
              <SectionTitle variant="h5">
                {t('help.privacyPolicy.sections.use.title')}
              </SectionTitle>
              <Typography variant="body1" paragraph>
                {t('help.privacyPolicy.sections.use.intro')}
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.use.items.access')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.use.items.process')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.use.items.notifications')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.use.items.improve')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.use.items.support')}
                </Typography>
              </Box>
            </Section>

            <Section>
              <SectionTitle variant="h5">{t('help.privacyPolicy.sections.protection.title')}</SectionTitle>
              <Typography variant="body1" paragraph>
                {t('help.privacyPolicy.sections.protection.intro')}
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.protection.items.ssl')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.protection.items.assessments')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.protection.items.access')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.protection.items.payment')}
                </Typography>
              </Box>
            </Section>

            <Section>
              <SectionTitle variant="h5">{t('help.privacyPolicy.sections.rights.title')}</SectionTitle>
              <Typography variant="body1" paragraph>
                {t('help.privacyPolicy.sections.rights.intro')}
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.rights.items.access')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.rights.items.correction')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.rights.items.deletion')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.rights.items.optout')}
                </Typography>
              </Box>
            </Section>

            <Section>
              <SectionTitle variant="h5">{t('help.privacyPolicy.sections.cookies.title')}</SectionTitle>
              <Typography variant="body1" paragraph>
                {t('help.privacyPolicy.sections.cookies.content')}
              </Typography>
            </Section>

            <Section>
              <SectionTitle variant="h5">{t('help.privacyPolicy.sections.children.title')}</SectionTitle>
              <Typography variant="body1" paragraph>
                {t('help.privacyPolicy.sections.children.content')}
              </Typography>
            </Section>

            <Section>
              <SectionTitle variant="h5">
                {t('help.privacyPolicy.sections.updates.title')}
              </SectionTitle>
              <Typography variant="body1" paragraph>
                {t('help.privacyPolicy.sections.updates.content')}
              </Typography>
            </Section>

            <Section>
              <SectionTitle variant="h5">
                {t('help.privacyPolicy.sections.author.title')}
              </SectionTitle>
              <Typography variant="body1" paragraph>
                {t('help.privacyPolicy.sections.author.intro')}
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.author.items.compensation')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.author.items.payments')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.author.items.reports')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.privacyPolicy.sections.author.items.model')}
                </Typography>
              </Box>
              <Typography variant="body1" paragraph>
                {t('help.privacyPolicy.sections.author.conclusion')}
              </Typography>
            </Section>

            <Section>
              <SectionTitle variant="h5">{t('help.privacyPolicy.sections.contact.title')}</SectionTitle>
              <Typography variant="body1" paragraph>
                {t('help.privacyPolicy.sections.contact.content')}
              </Typography>
            </Section>
          </ContentPaper>
        </Container>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;
